"use client";

import { Button } from "@shared/components/ui/button";
import { IconAlertTriangleFilled } from "@tabler/icons-react";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export type AppErrorPanelVariant = "segment" | "page";

const DEFAULT_MESSAGES: Record<AppErrorPanelVariant, string> = {
  segment:
    "An unexpected error occurred while loading this page. You can try again, or go back and continue from another page.",
  page: "An unexpected error occurred. You can try again, or return home and continue from there.",
};

export type AppErrorDigestProps = HTMLAttributes<HTMLParagraphElement> & {
  digest: string;
};

export const AppErrorDigest = ({ digest, className, ...rest }: AppErrorDigestProps) => {
  return (
    <p className={cn("font-mono text-xs text-muted-foreground/70", className)} {...rest}>
      Error digest · {digest}
    </p>
  );
};

export type AppErrorPanelProps = HTMLAttributes<HTMLDivElement> & {
  /** `segment` = in-app shell (min-height + inline digest). `page` = full-document body (parent owns chrome/digest). */
  variant?: AppErrorPanelVariant;
  title?: string;
  message?: string;
  onRetry: () => void;
  secondaryHref?: string;
  secondaryLabel?: string;
  digest?: string;
};

export const AppErrorPanel = ({
  variant = "segment",
  title = "Something went wrong",
  message,
  onRetry,
  secondaryHref = "/dashboard",
  secondaryLabel = "Go to dashboard",
  digest,
  className,
  ...rest
}: AppErrorPanelProps) => {
  const resolvedMessage = message ?? DEFAULT_MESSAGES[variant];

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-6 text-center",
        // Fill the AppInsetBody flex column so the block is vertically centered in the shell.
        // Less top padding (by half the site header) so optical center accounts for the context bar.
        variant === "segment" && "min-h-0 w-full flex-1 pb-12 pt-[calc(3rem-var(--header-height)/2)]",
        className,
      )}
      data-component="app-error-panel"
      data-variant={variant}
      {...rest}
    >
      <div className="mb-4 flex size-12 items-center justify-center text-(--ds-color-state-error) opacity-70 [&>svg]:size-full">
        <IconAlertTriangleFilled size={48} aria-hidden />
      </div>
      <h2 className="mb-2 text-lg font-semibold tracking-tight text-foreground">{title}</h2>
      <p className="m-0 max-w-100 text-sm leading-relaxed text-muted-foreground">{resolvedMessage}</p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <Button type="button" onClick={onRetry}>
          Try again
        </Button>
        <Button variant="outline" asChild>
          {/* Native anchor: safe when the App Router is partially broken (global-error). */}
          <a href={secondaryHref}>{secondaryLabel}</a>
        </Button>
      </div>
      {variant === "segment" && digest ? <AppErrorDigest digest={digest} className="mt-6" /> : null}
    </div>
  );
};
